/**
 * PRODINV #3005 slot S3.6 — WHAT ONE LOAD OF THIS PAGE COSTS OSTENDO.
 *
 * The page states its own query cost in prose, and the prose went stale the
 * moment a leg was added. It said FOUR statements per load from slot S1.2 until
 * this slot, while the endpoint has issued SEVEN since S1.4 and S1.5 landed the
 * On Order and In-Transfer legs. Nothing failed, because no test compared the
 * sentence with the endpoint.
 *
 * THE DIRECTION OF THE ERROR IS WHY IT MATTERED. Ostendo is shared and
 * rate-limited at an app-wide 100 reads an hour (`LIVE_CEILING_PER_HOUR`, the
 * client's IT number). A page that UNDERSTATES what it spends hands anyone
 * sizing the next feature a budget that is not there — four against seven is
 * 43% low, and the remaining headroom they would plan against does not exist.
 * Overstating costs a conversation; understating costs someone else's read.
 *
 * SO THE NUMBER IS DERIVED, NOT TYPED. This table is the one declaration of
 * which legs run against which Ostendo companies; the count is summed from it,
 * the page prints the sum, and `tests/product-inventory-endpoint-3005.test.ts`
 * dispatches the real route and asserts the measured statement count equals the
 * sum (the sentence's own pins are in `tests/product-inventory-cost-3005.test.ts`).
 * Add a leg here without touching the endpoint and the test fails; add one to
 * the endpoint without touching this table and the test fails too. Neither the
 * page nor a test hard-codes the number a second time.
 *
 * The server imports `dbs` from here for the two legs whose company list is a
 * single constant (`ON_ORDER_DB`, `IN_TRANSFER_DBS`). The on-hand and
 * batch-grain legs name CROS and COOP inline in their own `Promise.all`, which
 * this table does NOT rewrite — reshaping the hottest read on the page to feed
 * a prose sentence would be the tail wagging the dog. Those two are bound by
 * the measured-count test instead, which is the stronger guard anyway: it
 * counts what the endpoint actually sent.
 */

import { LIVE_CEILING_PER_HOUR } from "../../server/lib/ostendo-live-budget";

/** The two Ostendo companies this page reads. Mirrors `OstendoDb` in server/lib/ostendo-client. */
export type CostLegDb = "CROS" | "COOP";

/** One leg of the page's read, and the statements it costs. */
export interface OstendoStatementLeg {
  /** Stable key, for a test or a log line. */
  leg: "on_hand" | "batch" | "on_order" | "in_transfer";
  /**
   * One statement per entry. Two entries is one statement per company; one
   * entry is a leg that reads a single company and says why below.
   */
  dbs: readonly CostLegDb[];
  /**
   * The words the page's cost sentence must use for this leg. Pinned by test,
   * so a leg cannot be added to the count while staying unnamed in the prose.
   */
  phrase: string;
  /** Why it reads those companies and not others. */
  why: string;
}

export const PRODUCT_INVENTORY_STATEMENT_LEGS: readonly OstendoStatementLeg[] = Object.freeze([
  Object.freeze({
    leg: "on_hand",
    dbs: Object.freeze(["CROS", "COOP"] as const),
    phrase: "on-hand",
    why: "Stock sits in both companies' warehouses, so both are read and the board is live only when both answer.",
  }),
  Object.freeze({
    leg: "batch",
    dbs: Object.freeze(["CROS", "COOP"] as const),
    phrase: "batch-level",
    why: "The QC join is per batch, and it buys the per-item cross-check that proves the read was not clipped at the 1000-row ceiling.",
  }),
  Object.freeze({
    leg: "on_order",
    dbs: Object.freeze(["CROS"] as const),
    phrase: "On Order",
    why: "Customer demand is the distribution company's, and VW_CAI_OPEN_ORDERS does not exist on Manufacturing (Firebird -204, measured 7 Sep 2026).",
  }),
  Object.freeze({
    leg: "in_transfer",
    dbs: Object.freeze(["CROS", "COOP"] as const),
    phrase: "In-Transfer",
    why: "Stock moves on both sides, so a transfer can originate in either company.",
  }),
]) as readonly OstendoStatementLeg[];

/**
 * PALLETSRC. Statements this page sends to the nightly COPY, always, whatever
 * the routing decision was. Declared apart from the legs above because they
 * cost nothing from the live window: `ostendoSqlQuery` charges a slot only on
 * a live configuration. They are still upstream requests, so they are
 * declared, counted and pinned by the endpoint test like the live ones.
 */
export interface OstendoCopyStatementLeg {
  leg: "item_master";
  dbs: readonly CostLegDb[];
  phrase: string;
  why: string;
}

export const PRODUCT_INVENTORY_COPY_STATEMENT_LEGS: readonly OstendoCopyStatementLeg[] = Object.freeze([
  Object.freeze({
    leg: "item_master",
    dbs: Object.freeze(["CROS"] as const),
    phrase: "item master",
    why: "Bags per pallet is the -(N) suffix on the Sales company's item descriptions; Coopers drops it on several products. Master data, so the daily copy is current enough and the live window is left alone.",
  }),
]) as readonly OstendoCopyStatementLeg[];

/** Copy-only statements per load. ONE today. Not part of the live-window arithmetic below. */
export const PRODUCT_INVENTORY_COPY_STATEMENTS_PER_LOAD: number =
  PRODUCT_INVENTORY_COPY_STATEMENT_LEGS.reduce((n, leg) => n + leg.dbs.length, 0);

/** One Ostendo statement per (leg, company). SEVEN today, and summed rather than typed. */
export const PRODUCT_INVENTORY_STATEMENTS_PER_LOAD: number =
  PRODUCT_INVENTORY_STATEMENT_LEGS.reduce((n, leg) => n + leg.dbs.length, 0);

/** Statements per company, so a leg landing on the wrong one is visible. */
export const PRODUCT_INVENTORY_STATEMENTS_BY_DB: Readonly<Record<CostLegDb, number>> = Object.freeze(
  PRODUCT_INVENTORY_STATEMENT_LEGS.reduce(
    (acc, leg) => {
      for (const db of leg.dbs) acc[db] += 1;
      return acc;
    },
    { CROS: 0, COOP: 0 } as Record<CostLegDb, number>,
  ),
);

/**
 * The app-wide hourly ceiling on live Ostendo reads, re-exported rather than
 * retyped — the page printed a literal `100` before this slot, which is the
 * same staleness trap one number over. A test asserts the two agree.
 */
export const OSTENDO_LIVE_CEILING_PER_HOUR: number = LIVE_CEILING_PER_HOUR;

/**
 * How many uncached loads of this page exhaust the whole app's hour. FLOORED:
 * the fourteenth load fits and the fifteenth does not, so rounding up would
 * promise a load the window cannot serve.
 */
export const PRODUCT_INVENTORY_LOADS_PER_HOUR: number = Math.floor(
  OSTENDO_LIVE_CEILING_PER_HOUR / PRODUCT_INVENTORY_STATEMENTS_PER_LOAD,
);

const NUMBER_WORDS = [
  "zero", "one", "two", "three", "four", "five", "six", "seven", "eight", "nine", "ten",
  "eleven", "twelve", "thirteen", "fourteen", "fifteen", "sixteen", "seventeen", "eighteen",
  "nineteen", "twenty",
] as const;

/**
 * A small whole number as an English word, because the page's sentences read as
 * sentences. Falls back to the digits rather than throwing: a page that prints
 * `21` where it wanted `twenty-one` is a wording fault, and one that renders
 * nothing at all because a count grew is an outage.
 */
export function numberWord(n: number): string {
  return Number.isInteger(n) && n >= 0 && n < NUMBER_WORDS.length ? NUMBER_WORDS[n] : String(n);
}

/** `"seven"` — what the page prints for its per-load statement count. */
export const PRODUCT_INVENTORY_STATEMENTS_WORD: string = numberWord(PRODUCT_INVENTORY_STATEMENTS_PER_LOAD);

/** `"fourteen"` — uncached loads to the hour. */
export const PRODUCT_INVENTORY_LOADS_WORD: string = numberWord(PRODUCT_INVENTORY_LOADS_PER_HOUR);
